import {
	EntitlementDuration,
	type Feature,
	FeatureType,
	getGlobalMaxRedemption,
} from "@autumn/shared";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { CheckIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { cloneElement, isValidElement } from "react";
import { Button } from "@/components/v2/buttons/Button";
import { FormLabel } from "@/components/v2/form/FormLabel";
import { Input } from "@/components/v2/inputs/Input";
import { SearchableSelect } from "@/components/v2/selects/SearchableSelect";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/v2/selects/Select";
import { Separator } from "@/components/v2/separator";
import { SheetSection } from "@/components/v2/sheets/SharedSheetComponents";
import { useFeaturesQuery } from "@/hooks/queries/useFeaturesQuery";
import { cn } from "@/lib/utils";
import { getFeatureIconConfig } from "@/views/products/features/utils/getFeatureIcon";
import type {
	FrontendReward,
	FrontendRewardEntitlement,
} from "../../types/frontendReward";

interface FeatureGrantRewardConfigProps {
	reward: FrontendReward;
	setReward: (reward: FrontendReward) => void;
}

export function FeatureGrantRewardConfig({
	reward,
	setReward,
}: FeatureGrantRewardConfigProps) {
	const { features } = useFeaturesQuery();

	const availableGrantTargets = features.filter(
		(f) =>
			f.type === FeatureType.Metered ||
			f.type === FeatureType.CreditSystem ||
			f.type === FeatureType.Boolean,
	);

	const isBooleanFeature = (featureId: string) =>
		features.find((f) => f.id === featureId)?.type === FeatureType.Boolean;

	const entitlements = reward.featureGrantEntitlements;

	const updateEntitlement = ({
		index,
		updates,
	}: {
		index: number;
		updates: Partial<FrontendRewardEntitlement>;
	}) => {
		const updated = [...entitlements];
		updated[index] = { ...updated[index], ...updates };
		setReward({ ...reward, featureGrantEntitlements: updated });
	};

	const addEntitlement = () => {
		const existingExpiry = entitlements.find((e) => e.expiry)?.expiry;
		setReward({
			...reward,
			featureGrantEntitlements: [
				...entitlements,
				{
					feature_id: "",
					allowance: 0,
					expiry: existingExpiry ? { ...existingExpiry } : undefined,
				},
			],
		});
	};

	const removeEntitlement = ({ index }: { index: number }) => {
		setReward({
			...reward,
			featureGrantEntitlements: entitlements.filter((_, i) => i !== index),
		});
	};

	const updatePromoCode = ({
		index,
		code,
	}: {
		index: number;
		code: string;
	}) => {
		const updated = [...(reward.promo_codes || [])];
		updated[index] = { ...updated[index], code };
		setReward({ ...reward, promo_codes: updated });
	};

	const updateMaxRedemptions = ({
		index,
		value,
	}: {
		index: number;
		value: number | undefined;
	}) => {
		const updated = [...(reward.promo_codes || [])];
		const promoCode = updated[index] ?? { code: "" };
		const { max_redemptions: _maxRedemptions, ...rest } = promoCode;
		updated[index] = {
			...rest,
			global_max_redemption: value,
		};
		setReward({ ...reward, promo_codes: updated });
	};

	const addPromoCode = () => {
		const existingMax = reward.promo_codes
			?.map((pc) => getGlobalMaxRedemption(pc))
			.find((max) => max !== undefined);
		setReward({
			...reward,
			promo_codes: [
				...(reward.promo_codes || []),
				{ code: "", global_max_redemption: existingMax },
			],
		});
	};

	const removePromoCode = ({ index }: { index: number }) => {
		setReward({
			...reward,
			promo_codes: (reward.promo_codes || []).filter((_, i) => i !== index),
		});
	};

	const getAvailableFeatures = ({ currentIndex }: { currentIndex: number }) => {
		const selectedIds = entitlements
			.filter((_, i) => i !== currentIndex)
			.map((e) => e.feature_id);
		return availableGrantTargets.filter((f) => !selectedIds.includes(f.id));
	};

	const getGrantTargetIcon = ({ feature }: { feature: Feature }) => {
		const iconConfig = getFeatureIconConfig(
			feature.type,
			feature.config?.usage_type,
		);

		if (isValidElement<{ className?: string }>(iconConfig.icon)) {
			return cloneElement(iconConfig.icon, {
				className: cn(iconConfig.icon.props.className, iconConfig.color),
			});
		}

		return iconConfig.icon;
	};

	return (
		<>
			{/* Promo Codes Section */}
			<SheetSection title="Promo Codes" withSeparator={false}>
				<div className="space-y-3">
					{(reward.promo_codes || []).map((promoCode, index) => (
						<div key={index} className="space-y-2">
							<div className="flex items-end gap-2">
								<div className="flex-1">
									{index === 0 && <FormLabel>Code</FormLabel>}
									<Input
										value={promoCode.code}
										onChange={(e) =>
											updatePromoCode({
												index,
												code: e.target.value
													.toUpperCase()
													.replace(/[^A-Z0-9]/g, ""),
											})
										}
										placeholder="PROMO2024"
									/>
								</div>
								<div className="w-32">
									{index === 0 && <FormLabel>Max Uses</FormLabel>}
									<Input
										type="number"
										value={getGlobalMaxRedemption(promoCode) ?? ""}
										onChange={(e) =>
											updateMaxRedemptions({
												index,
												value: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										placeholder="Unlimited"
									/>
								</div>
								{(reward.promo_codes || []).length > 1 && (
									<button
										type="button"
										onClick={() => removePromoCode({ index })}
										className="p-2 text-subtle hover:text-foreground transition-colors"
									>
										<TrashIcon size={14} />
									</button>
								)}
							</div>
						</div>
					))}
					<Button variant="secondary" size="sm" onClick={addPromoCode}>
						<PlusIcon size={12} className="mr-1" />
						Add Code
					</Button>
				</div>
			</SheetSection>
			<div className="px-4">
				<Separator />
			</div>

			{/* Entitlements Section */}
			<SheetSection title="Feature Grants" withSeparator={false}>
				<div className="space-y-4">
					<AnimatePresence initial={false}>
						{entitlements.map((ent, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, scaleY: 0.95, originY: 0 }}
								animate={{ opacity: 1, scaleY: 1 }}
								exit={{ opacity: 0, scaleY: 0.95 }}
								transition={{ duration: 0.2, ease: "easeOut" }}
							>
								<div className="relative space-y-3 rounded-lg border border-border p-3">
									{entitlements.length > 1 && (
										<button
											type="button"
											onClick={() => removeEntitlement({ index })}
											className="absolute top-2 right-2 p-1 text-subtle hover:text-foreground transition-colors cursor-pointer"
										>
											<TrashIcon size={12} />
										</button>
									)}

									{/* Feature selector */}
									<div>
										<FormLabel>Feature</FormLabel>
										<SearchableSelect<Feature>
											value={ent.feature_id || null}
											onValueChange={(value) =>
												updateEntitlement({
													index,
													updates: { feature_id: value },
												})
											}
											options={getAvailableFeatures({ currentIndex: index })}
											getOptionValue={(f) => f.id}
											getOptionLabel={(f) => f.name}
											renderOption={(feature, isSelected) => (
												<>
													{getGrantTargetIcon({ feature })}
													<span className="flex-1 truncate min-w-0">
														{feature.name}
													</span>
													{isSelected && (
														<CheckIcon className="size-4 shrink-0" />
													)}
												</>
											)}
											renderValue={(feature) => {
												if (!feature) {
													return (
														<span className="text-tertiary-foreground leading-none">
															Select a feature or credit system...
														</span>
													);
												}

												return (
													<span className="flex items-center gap-2 min-w-0 max-w-full leading-none">
														{getGrantTargetIcon({ feature })}
														<span className="truncate">{feature.name}</span>
													</span>
												);
											}}
											placeholder="Select a feature or credit system..."
											searchable
											searchPlaceholder="Search features and credit systems..."
											emptyText="No eligible features found"
											triggerClassName="cursor-pointer h-input"
											contentClassName="[&_[cmdk-group]]:py-0"
										/>
									</div>

									{/* Allowance (boolean features grant on/off access, no balance) */}
									{!isBooleanFeature(ent.feature_id) && (
										<div>
											<FormLabel>Balance Grant</FormLabel>
											<Input
												type="number"
												value={ent.allowance || ""}
												onChange={(e) =>
													updateEntitlement({
														index,
														updates: { allowance: Number(e.target.value) },
													})
												}
												placeholder="0"
												className="w-full [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
											/>
										</div>
									)}

									{/* Expiry */}
									<div>
										<FormLabel>Expiry</FormLabel>
										{ent.expiry ? (
											<div className="flex items-center gap-2">
												<Input
													type="number"
													value={ent.expiry.length || ""}
													onChange={(e) =>
														updateEntitlement({
															index,
															updates: {
																expiry: {
																	duration:
																		ent.expiry?.duration ??
																		EntitlementDuration.Month,
																	length: Number(e.target.value),
																},
															},
														})
													}
													placeholder="30"
													className="w-20"
												/>
												<Select
													value={ent.expiry.duration}
													onValueChange={(value) =>
														updateEntitlement({
															index,
															updates: {
																expiry: {
																	duration: value as EntitlementDuration,
																	length: ent.expiry?.length ?? 1,
																},
															},
														})
													}
													items={{
														[EntitlementDuration.Day]: "Day(s)",
														[EntitlementDuration.Week]: "Week(s)",
														[EntitlementDuration.Month]: "Month(s)",
														[EntitlementDuration.Year]: "Year(s)",
													}}
												>
													<SelectTrigger className="flex-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value={EntitlementDuration.Day}>
															Day(s)
														</SelectItem>
														<SelectItem value={EntitlementDuration.Week}>
															Week(s)
														</SelectItem>
														<SelectItem value={EntitlementDuration.Month}>
															Month(s)
														</SelectItem>
														<SelectItem value={EntitlementDuration.Year}>
															Year(s)
														</SelectItem>
													</SelectContent>
												</Select>
												<button
													type="button"
													onClick={() =>
														updateEntitlement({
															index,
															updates: { expiry: undefined },
														})
													}
													className="text-xs text-subtle hover:text-foreground transition-colors whitespace-nowrap"
												>
													Clear
												</button>
											</div>
										) : (
											<button
												type="button"
												onClick={() =>
													updateEntitlement({
														index,
														updates: {
															expiry: {
																duration: EntitlementDuration.Month,
																length: 1,
															},
														},
													})
												}
												className="text-xs text-subtle hover:text-foreground transition-colors cursor-pointer"
											>
												No expiry (permanent). Click to set one.
											</button>
										)}
									</div>
								</div>
							</motion.div>
						))}
					</AnimatePresence>

					<Button variant="secondary" size="sm" onClick={addEntitlement}>
						<PlusIcon size={12} className="mr-1" />
						Add Grant
					</Button>
				</div>
			</SheetSection>
		</>
	);
}
